import { useEffect, useState } from "react";

/**
 * Privacy blur — al perder foco la ventana (cambiar de tab, lock screen),
 * blureamos el contenido para que datos sensibles (pasajeros, expenses, AOG)
 * no queden expuestos si dejás el celular en una mesa.
 *
 * Devuelve `true` cuando la ventana tiene foco (contenido nítido).
 *
 * Vive en un hook propio porque la lógica de reconciliación es sutil y hay
 * que poder testearla sin montar el layout entero.
 */
export default function usePrivacyBlur() {
  // Estado inicial desde `document.hasFocus()`, no `true` a ciegas: si la
  // pestaña se abre/restaura en segundo plano, asumir foco deja contenido
  // sensible sin blur hasta el primer evento.
  const [windowFocused, setWindowFocused] = useState(
    () => typeof document === "undefined" || document.hasFocus()
  );

  useEffect(() => {
    // Debounce el un-blur 300 ms: el Snipping Tool de Win11 usa un overlay
    // layered transparent que deja pasar el click-through al browser de abajo
    // mid-snip → dispara `window.focus` → quitaba el blur antes de capturar.
    // Aplicar blur es instantáneo (queremos protección inmediata), quitarlo
    // espera 300 ms para absorber el rebote de foco. Si llega otro `blur`
    // dentro de la ventana de 300 ms (overlay reasume foreground), se cancela
    // el un-blur y el contenido queda bluereado durante toda la captura.
    let unblurTimer = null;
    const clearTimer = () => {
      if (unblurTimer) {
        clearTimeout(unblurTimer);
        unblurTimer = null;
      }
    };
    const applyBlur = () => {
      clearTimer();
      setWindowFocused(false);
    };
    const scheduleUnblur = () => {
      clearTimer();
      unblurTimer = setTimeout(() => {
        setWindowFocused(true);
        unblurTimer = null;
      }, 300);
    };

    // Red de seguridad contra el blur ATORADO. `window.focus` no es confiable
    // como única vía de recuperación: al restaurar la ventana tras suspender
    // la PC o desminimizar, el navegador puede repintar la página sin emitir
    // `focus` (el foco del SO aterriza en otra app o en un panel del propio
    // browser). Sin esto, un solo `focus` perdido dejaba el estado en false
    // PARA SIEMPRE — el blur sobrevivía a cambiar de pestaña del sidebar (el
    // filter vive en el layout, no en la página) y sólo se quitaba recargando
    // o reabriendo la app.
    //
    // `visibilitychange` sí se dispara al volver la pestaña al frente, y
    // `document.hasFocus()` es la fuente de verdad consultable — juntos
    // cubren el caso en que los eventos sueltos se pierden.
    const syncFromDocument = () => {
      if (document.visibilityState !== "visible") {
        applyBlur();
        return;
      }
      if (document.hasFocus()) scheduleUnblur();
    };

    window.addEventListener("blur", applyBlur);
    window.addEventListener("focus", scheduleUnblur);
    document.addEventListener("visibilitychange", syncFromDocument);
    // El click/tecla también reconcilian: si el usuario está interactuando, la
    // ventana tiene foco por definición, sin importar qué eventos se
    // perdieron. Es la última red — cualquier interacción destraba el blur.
    window.addEventListener("pointerdown", syncFromDocument);
    window.addEventListener("keydown", syncFromDocument);

    return () => {
      clearTimer();
      window.removeEventListener("blur", applyBlur);
      window.removeEventListener("focus", scheduleUnblur);
      document.removeEventListener("visibilitychange", syncFromDocument);
      window.removeEventListener("pointerdown", syncFromDocument);
      window.removeEventListener("keydown", syncFromDocument);
    };
  }, []);

  return windowFocused;
}
